import dayjs from "dayjs";
import isSameOrBefore from "dayjs/plugin/isSameOrBefore";
import isSameOrAfter from "dayjs/plugin/isSameOrAfter";
import { BACKEND_DATE_FORMAT } from "../../../consts/index.js";

dayjs.extend(isSameOrBefore);
dayjs.extend(isSameOrAfter);

const iso = (d) => dayjs(d).format(BACKEND_DATE_FORMAT);

// --- Temperature helpers ---
function pickNum(x) {
  const n = Number(x);
  return Number.isFinite(n) ? n : null;
}
function getMinMax(t) {
  // Support {max,min} or {tmax,tmin}
  const max = pickNum(t.max ?? t.tmax);
  const min = pickNum(t.min ?? t.tmin);
  return { max, min };
}
function dailyGDD(t, base = 5) {
  const { max, min } = getMinMax(t);
  if (max == null || min == null) return 0;
  const g = (max + min) / 2 - base;
  return g > 0 ? g : 0;
}
function makeDailyGddMap(temps, base) {
  const m = new Map();
  for (const t of temps) m.set(iso(t.date), dailyGDD(t, base));
  return m;
}
function sumGddRange(gddByDate, startExclusiveISO, endInclusiveISO) {
  let sum = 0;
  for (
    let cur = dayjs(startExclusiveISO).add(1, "day");
    cur.isSameOrBefore(dayjs(endInclusiveISO), "day");
    cur = cur.add(1, "day")
  ) {
    sum += gddByDate.get(iso(cur)) ?? 0;
  }
  return sum;
}

// --- Series helpers for your structure ---
const sortByDate = (a, b) => (a.date < b.date ? -1 : a.date > b.date ? 1 : 0);

function upsertPoint(points, dateISO, avgLeafCm) {
  const idx = points.findIndex((p) => p.date === dateISO);
  const value = Number.isFinite(avgLeafCm)
    ? Number(avgLeafCm.toFixed(1))
    : avgLeafCm;

  if (idx >= 0) {
    // keep other fields (like isHarvested) intact
    points[idx] = { ...points[idx], avgLeafCm: value };
  } else {
    points.push({ date: dateISO, isHarvested: false, avgLeafCm: value });
  }
}

/**
 * @param {{
 *   unharvestedData: Array<{
 *     bayId: string|number,
 *     rowLabel?: string,
 *     rowNumber?: number,
 *     points: Array<{date: string, isHarvested: boolean, avgLeafCm: number|null}>
 *   }>,
 *   forecastData: Array<{date: string, max?: number, min?: number, tmax?: number, tmin?: number}>,
 *   forecastDays?: number,
 *   gddBase?: number,
 *   startISO?: string|null,
 *   endISO?: string|null,
 *   selectedDate?: string|null
 * }} args
 */
export function forecastFillService({
  unharvestedData,
  forecastData,
  forecastDays = 5,
  gddBase = 5,
  startISO = null,
  endISO = null,
  selectedDate = null,
}) {
  console.log("unharvestedData", unharvestedData);
  console.log("forecastData", forecastData);
  if (!Array.isArray(unharvestedData) || !Array.isArray(forecastData)) {
    throw new Error("unharvestedData and forecastData must be arrays.");
  }

  const gddByDate = makeDailyGddMap(forecastData, gddBase);

  // Shallow copy rows; clone/sort points per row so original input isn't mutated
  const out = unharvestedData.map((row) => ({
    ...row,
    points: [...(row.points || [])].sort(sortByDate),
  }));

  for (const row of out) {
    // valid historical points
    let hist = row.points
      .filter((p) => Number.isFinite(p?.avgLeafCm))
      .sort(sortByDate);

    if (selectedDate) {
      const sel = dayjs(selectedDate, BACKEND_DATE_FORMAT);
      const trimmed = hist.filter((p) =>
        dayjs(p.date).isSameOrBefore(sel, "day"),
      );
      if (trimmed.length >= 2) hist = trimmed;
    }

    if (hist.length < 2) continue;

    const first = hist[0];
    const last = hist[hist.length - 1];

    if (dayjs(first.date).isAfter(dayjs(last.date), "day")) continue;

    const totalAvgLeafSize = (last.avgLeafCm ?? 0) - (first.avgLeafCm ?? 0);
    const gddHist = sumGddRange(gddByDate, iso(first.date), iso(last.date));
    if (!(gddHist > 0)) continue; // no heat -> skip; change to carry-forward if you prefer

    // Forecast exactly N days after last
    let rollingCum = 0;
    for (let d = 1; d <= forecastDays; d += 1) {
      const target = dayjs(last.date).add(d, "day");
      if (endISO && target.isAfter(dayjs(endISO), "day")) break;

      const targetISO = iso(target);
      rollingCum += gddByDate.get(targetISO) ?? 0;

      const inc = totalAvgLeafSize * (rollingCum / gddHist);
      const predicted = (last.avgLeafCm ?? 0) + inc;

      upsertPoint(row.points, targetISO, predicted);
    }

    row.points.sort(sortByDate);
  }

  return out;
}
